import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ReplayIcon from '@mui/icons-material/Replay';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { StorePicker } from '../../components/StorePicker';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import {
  ControlPogResetMappingRun,
  ControlPogResetTasksResponse,
  ControlPogResetTaskSummary,
  PogResetTaskStatusFilter,
  Store,
} from '../../types';
import { PogResetFlagBadge } from './components/PogResetFlagBadge';

const RUN_POLL_INTERVAL_MS = 5000;
const STATUS_FILTERS: PogResetTaskStatusFilter[] = ['open', 'closed', 'all'];

// Compact labels for the subordinate issue chips shown next to the status badge.
const ISSUE_SHORT_LABELS: Record<string, string> = {
  wrong_sequence: 'Wrong seq',
  duplicate_candidate: 'Duplicate',
  multiple_similar_candidates: 'Similar',
  missing_candidate: 'Missing',
};

export const PogResetPage: React.FC = () => {
  const { storeName } = useParams<{ storeName: string }>();
  const navigate = useNavigate();
  const { token } = useAuthContext();
  const { toggleLoading, showErrorToast, showSuccessToast } = useNotificationContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [store, setStore] = useState<Store | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [tasksResponse, setTasksResponse] = useState<ControlPogResetTasksResponse | null>(null);
  // Filters/sort/search live in the URL so views are shareable and survive refresh.
  const [statusFilter, setStatusFilter] = useState<PogResetTaskStatusFilter>(() => {
    const value = searchParams.get('status');
    return value === 'closed' || value === 'all' ? value : 'open';
  });
  const [activeRun, setActiveRun] = useState<ControlPogResetMappingRun | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [badgeFilter, setBadgeFilter] = useState<Set<string>>(
    () => new Set((searchParams.get('badges') ?? '').split(',').filter(Boolean))
  );
  const [scoreSort, setScoreSort] = useState<{
    key: 'averageScore' | 'averageCoverage';
    dir: 'desc' | 'asc';
  } | null>(() => {
    const [key, dir] = (searchParams.get('sort') ?? '').split(':');
    if (
      (key === 'averageScore' || key === 'averageCoverage') &&
      (dir === 'asc' || dir === 'desc')
    ) {
      return { key, dir };
    }
    return null;
  });
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [hideReviewed, setHideReviewed] = useState(() => searchParams.get('reviewed') === 'hide');
  const pollFailuresRef = useRef(0);
  const isFirstRunRef = useRef(true);

  // Reflect the current view in the URL (replace, to avoid history spam).
  useEffect(() => {
    const params: Record<string, string> = {};
    if (statusFilter !== 'open') params.status = statusFilter;
    if (badgeFilter.size > 0) params.badges = [...badgeFilter].join(',');
    if (scoreSort) params.sort = `${scoreSort.key}:${scoreSort.dir}`;
    if (search.trim()) params.q = search.trim();
    if (hideReviewed) params.reviewed = 'hide';
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, badgeFilter, scoreSort, search, hideReviewed]);

  const loadData = async (silent = false) => {
    if (!silent) toggleLoading(true);
    try {
      const stores = await ApiService.fetchStores(token);
      setAllStores(stores);
      // Reached via the store-less /pog-reset shortcut: show the store picker only.
      if (!storeName) return;
      const currentStore = stores.find((s) => s.storeName === storeName);
      if (!currentStore) {
        showErrorToast('Store not found');
        navigate('/stores');
        return;
      }
      setStore(currentStore);
      const response = await ApiService.fetchPogResetTasks(token, storeName, statusFilter);
      setTasksResponse(response);
      setActiveRun(response.activeRun?.status === 'running' ? response.activeRun : null);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error loading POG reset tasks');
    } finally {
      if (!silent) toggleLoading(false);
    }
  };

  useEffect(() => {
    setStore(null);
    setTasksResponse(null);
    setActiveRun(null);
    // Keep URL-restored filters on first render; reset them on store/tab switches.
    if (!isFirstRunRef.current) {
      setBadgeFilter(new Set());
    }
    isFirstRunRef.current = false;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeName, token, statusFilter]);

  // Poll the active run until it reaches a terminal status, then reload the task list.
  useEffect(() => {
    if (!storeName || !activeRun || activeRun.status !== 'running') return;
    const runId = activeRun.runId;
    pollFailuresRef.current = 0;
    const intervalId = window.setInterval(async () => {
      try {
        const run = await ApiService.fetchPogResetMappingRun(token, storeName, runId);
        pollFailuresRef.current = 0;
        if (run.status === 'completed') {
          setActiveRun(null);
          showSuccessToast('Mapping calculation completed');
          await loadData(true);
        } else if (run.status === 'failed') {
          setActiveRun(null);
          showErrorToast(`Mapping calculation failed: ${run.error ?? 'unknown error'}`);
          await loadData(true);
        }
      } catch (err) {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= 3) {
          setActiveRun(null);
          showErrorToast(
            err instanceof Error ? err.message : 'Lost track of the mapping calculation run'
          );
        }
      }
    }, RUN_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.runId, activeRun?.status, storeName, token]);

  const handleGenerateMappings = async (force: boolean) => {
    if (!storeName || isTriggering) return;
    setIsTriggering(true);
    try {
      const run = await ApiService.startPogResetMappingRun(token, storeName, { force });
      if (run.status === 'running') {
        setActiveRun(run);
        showSuccessToast(
          'Mapping calculation started — it runs in the background, results will appear here in a few minutes'
        );
      } else {
        // Cached-only runs can complete before the response returns.
        await loadData(true);
        if (run.status === 'failed') {
          showErrorToast(`Mapping calculation failed: ${run.error ?? 'unknown error'}`);
        } else {
          showSuccessToast('Mapping calculation completed');
        }
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error starting mapping calculation');
    } finally {
      setIsTriggering(false);
    }
  };

  const isRunning = activeRun?.status === 'running';
  const tasks = useMemo(() => tasksResponse?.tasks ?? [], [tasksResponse]);

  // "Generate mappings" (non-force) only computes open tasks without cached results —
  // hide it when there is nothing for it to do. "Recalculate everything" stays.
  const hasUnprocessedOpenTasks = tasks.some((task) => task.status === 'open' && !task.mapping);

  // Two axes per task (computed backend-side): rolled-up match status (triage)
  // + its issue flags (diagnosis).
  const taskStatus = (task: ControlPogResetTaskSummary): string =>
    task.mapping?.status ?? 'not_processed';
  const taskIssues = (task: ControlPogResetTaskSummary): string[] => task.mapping?.issues ?? [];
  const taskFilterBadges = (task: ControlPogResetTaskSummary): string[] => [
    taskStatus(task),
    ...taskIssues(task),
  ];

  const availableBadges = useMemo(() => {
    const counts = new Map<string, number>();
    tasks.forEach((task) =>
      taskFilterBadges(task).forEach((badge) => counts.set(badge, (counts.get(badge) ?? 0) + 1))
    );
    // Status chips first (triage), then issue chips (diagnosis).
    const ordered = [
      'matched',
      'partially_matched',
      'no_match',
      'not_processed',
      'wrong_sequence',
      'duplicate_candidate',
      'multiple_similar_candidates',
      'missing_candidate',
    ];
    const badges = [
      ...ordered.filter((badge) => counts.has(badge)),
      ...[...counts.keys()].filter((badge) => !ordered.includes(badge)),
    ];
    return badges.map((badge) => ({ badge, count: counts.get(badge) ?? 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    const query = search.trim().toLowerCase();
    let matching =
      badgeFilter.size === 0
        ? tasks
        : tasks.filter((task) => taskFilterBadges(task).some((badge) => badgeFilter.has(badge)));
    if (hideReviewed) {
      matching = matching.filter((task) => !task.reviewed);
    }
    if (query) {
      matching = matching.filter((task) =>
        [
          task.pogName,
          task.pogNumber,
          String(task.taskId),
          ...(task.mapping?.candidateTagNames ?? []),
        ].some((value) => value?.toLowerCase().includes(query))
      );
    }
    if (!scoreSort) return matching;
    return [...matching].sort((a, b) => {
      const scoreA = a.mapping?.[scoreSort.key] ?? -1;
      const scoreB = b.mapping?.[scoreSort.key] ?? -1;
      return scoreSort.dir === 'desc' ? scoreB - scoreA : scoreA - scoreB;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, badgeFilter, scoreSort, search, hideReviewed]);

  // Most recent calculation timestamp across all rows.
  const lastComputedAt = useMemo(() => {
    const timestamps = tasks
      .map((task) => task.mapping?.computedAt)
      .filter((value): value is string => Boolean(value));
    if (timestamps.length === 0) return null;
    return timestamps.reduce((latest, value) => (value > latest ? value : latest));
  }, [tasks]);

  // Averages across the visible rows that have mappings.
  const visibleAverages = useMemo(() => {
    const scores = filteredTasks
      .map((task) => task.mapping?.averageScore)
      .filter((score): score is number => score != null);
    const coverages = filteredTasks
      .map((task) => task.mapping?.averageCoverage)
      .filter((coverage): coverage is number => coverage != null);
    if (scores.length === 0 && coverages.length === 0) return null;
    const mean = (values: number[]) =>
      values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
    return { score: mean(scores), coverage: mean(coverages), count: scores.length };
  }, [filteredTasks]);

  const cycleSort = (key: 'averageScore' | 'averageCoverage') => {
    setScoreSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'desc' };
      return prev.dir === 'desc' ? { key, dir: 'asc' } : null;
    });
  };

  const sortIndicator = (key: 'averageScore' | 'averageCoverage') =>
    scoreSort?.key === key ? (scoreSort.dir === 'desc' ? ' ↓' : ' ↑') : '';

  const toggleBadgeFilter = (badge: string) => {
    setBadgeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(badge)) next.delete(badge);
      else next.add(badge);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-[96rem] mx-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <Link
              to={storeName ? `/stores/${storeName}` : '/stores'}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
            >
              <ArrowBackIcon />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Automated Planogram Reset</h1>
              <p className="text-sm text-gray-600">
                {storeName
                  ? `${store?.legibleName ?? storeName}${
                      store
                        ? ` · ${store.chainLegibleName} (${store.chainName}, ${store.storeName})`
                        : ''
                    }`
                  : 'Select a store to view its POG reset tasks'}
              </p>
            </div>
          </div>
          <StorePicker
            stores={allStores}
            current={store}
            onSelect={(s) => navigate(`/stores/${s.storeName}/pog-reset`)}
          />
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex items-center gap-1">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setStatusFilter(filter)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${
                    statusFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search POG name / number / task id / mod tag"
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
            <label className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap">
              <input
                type="checkbox"
                checked={hideReviewed}
                onChange={(e) => setHideReviewed(e.target.checked)}
              />
              Hide reviewed
            </label>
            {availableBadges.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap md:ml-3">
                {availableBadges.map(({ badge, count }) => (
                  <button
                    key={badge}
                    onClick={() => toggleBadgeFilter(badge)}
                    className={`rounded-full ${
                      badgeFilter.has(badge)
                        ? 'ring-2 ring-blue-500 ring-offset-1'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                    title="Filter by status"
                  >
                    <PogResetFlagBadge flag={badge} suffix={`(${count})`} />
                  </button>
                ))}
                {badgeFilter.size > 0 && (
                  <button
                    onClick={() => setBadgeFilter(new Set())}
                    className="text-xs text-blue-600 hover:underline ml-1"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
            <div className="flex-1" />
            {lastComputedAt && !isRunning && (
              <span
                className="text-xs text-gray-500 whitespace-nowrap"
                title="Most recent mapping calculation across the listed tasks"
              >
                Last calculated: {new Date(lastComputedAt).toLocaleString()}
              </span>
            )}
            {isRunning && (
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                Mapping calculation running…
              </div>
            )}
            {hasUnprocessedOpenTasks && (
              <button
                onClick={() => handleGenerateMappings(false)}
                disabled={isRunning || isTriggering || !store}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <AutoFixHighIcon fontSize="small" />
                Generate mappings
              </button>
            )}
            <button
              onClick={() => handleGenerateMappings(true)}
              disabled={isRunning || isTriggering || !store}
              title="Recompute candidates even for tasks that already have cached results"
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ReplayIcon fontSize="small" />
              Recalculate everything
            </button>
          </div>
        </div>

        {!storeName && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No store selected — pick one from the store picker in the top-right corner.
          </div>
        )}

        {storeName && (
          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-gray-600">
                  <th className="px-4 py-3 font-medium">Task ID</th>
                  <th className="px-4 py-3 font-medium">POG Start date</th>
                  <th className="px-4 py-3 font-medium">Planogram</th>
                  <th className="px-4 py-3 font-medium">Mods</th>
                  <th className="px-4 py-3 font-medium">Task</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => cycleSort('averageScore')}
                      className="hover:text-gray-900"
                      title="Mean best-candidate score across mods (how purely camera mods belong to the planogram)"
                    >
                      Avg Score{sortIndicator('averageScore')}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => cycleSort('averageCoverage')}
                      className="hover:text-gray-900"
                      title="Mean planogram coverage across mods (matched / planogram items)"
                    >
                      Avg Coverage{sortIndicator('averageCoverage')}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">Mappings</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => (
                  <tr
                    key={task.taskId}
                    onClick={() =>
                      navigate(`/stores/${storeName}/pog-reset/${task.taskId}`, {
                        state: { taskIds: filteredTasks.map((t) => t.taskId) },
                      })
                    }
                    className={`border-b last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                      task.reviewed ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-mono">{task.taskId}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{task.pogStartDate ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div>{task.pogName ?? '—'}</div>
                      <div className="text-xs text-gray-500">{task.pogNumber ?? ''}</div>
                    </td>
                    <td className="px-4 py-3">{task.modCount ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            task.status === 'open'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {task.status}
                        </span>
                        {task.reviewed && (
                          <span
                            className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                            title={
                              task.reviewedAt
                                ? `Reviewed ${new Date(task.reviewedAt).toLocaleString()}`
                                : 'Reviewed'
                            }
                          >
                            Reviewed
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PogResetFlagBadge flag={taskStatus(task)} />
                        {taskIssues(task).length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap opacity-80">
                            {taskIssues(task).map((issue) => (
                              <span
                                key={issue}
                                className="text-[11px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 whitespace-nowrap"
                                title={issue}
                              >
                                {ISSUE_SHORT_LABELS[issue] ?? issue}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {task.mapping?.averageScore != null
                        ? `${Math.round(task.mapping.averageScore * 100)}%`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {task.mapping?.averageCoverage != null
                        ? `${Math.round(task.mapping.averageCoverage * 100)}%`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {task.mapping ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-600">
                            {task.mapping.matchedModCount}/{task.mapping.scoredModCount} mods
                          </span>
                          {task.mapping.computedAt && (
                            <span
                              className="text-xs text-gray-400"
                              title={`run ${task.mapping.runId ?? ''} (${task.mapping.algorithmVersion ?? ''})`}
                            >
                              {new Date(task.mapping.computedAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {tasksResponse && filteredTasks.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                      No {statusFilter === 'all' ? '' : `${statusFilter} `}POG reset tasks found for
                      this store.
                    </td>
                  </tr>
                )}
              </tbody>
              {visibleAverages && (
                <tfoot>
                  <tr className="border-t bg-gray-50 font-medium">
                    <td colSpan={6} className="px-4 py-3 text-right text-gray-600">
                      Average of {visibleAverages.count} mapped task
                      {visibleAverages.count === 1 ? '' : 's'}:
                    </td>
                    <td className="px-4 py-3">
                      {visibleAverages.score != null
                        ? `${Math.round(visibleAverages.score * 100)}%`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {visibleAverages.coverage != null
                        ? `${Math.round(visibleAverages.coverage * 100)}%`
                        : '—'}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
